// Production > Overview dashboard. Single-fetch render of all the headline
// numbers an admin wants at a glance: counts by status, revenue, recent
// orders, recent production activity, top products, top states. Everything
// narrows to the date range chosen in the top toolbar.
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../../lib/api'
import DateRangeFilter, { RangeValue } from '../../components/admin/DateRangeFilter'

interface OverviewData {
  range: { from: string; to: string }
  counts: {
    pendingPayment: number; paid: number; inProduction: number
    shipped: number; delivered: number; cancelled: number
    artworkAwaiting: number
  }
  revenue: { cents: number; orders: number }
  recentOrders: Array<{
    id: string; number: number; status: string; totalCents: number; createdAt: string
    customerEmail: string | null; customerName: string | null
  }>
  activity: Array<{ id: string; orderId: string; orderNumber: number; kind: string; note: string | null; createdAt: string; byEmail: string | null }>
  topProducts: Array<{ productId: string; slug: string; kind: string; name: string; units: number; lineItems: number; revenueCents: number }>
  topStates: Array<{ state: string; count: number; revenueCents: number }>
}

const STATUS_LABELS: Record<string, { label: string; tone: string }> = {
  pending_payment: { label: 'Pending payment', tone: 'bg-amber-100 text-amber-800' },
  paid:            { label: 'Paid',            tone: 'bg-emerald-100 text-emerald-800' },
  in_production:   { label: 'In production',   tone: 'bg-blue-100 text-blue-800' },
  shipped:         { label: 'Shipped',         tone: 'bg-indigo-100 text-indigo-800' },
  delivered:       { label: 'Delivered',       tone: 'bg-teal-100 text-teal-800' },
  cancelled:       { label: 'Cancelled',       tone: 'bg-red-100 text-red-700' },
  refunded:        { label: 'Refunded',        tone: 'bg-rose-100 text-rose-800' },
}
export function StatusBadge({ status }: { status: string }) {
  const s = STATUS_LABELS[status] ?? { label: status, tone: 'bg-gray-100 text-gray-700' }
  return <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${s.tone}`}>{s.label}</span>
}

export default function AdminOverview() {
  const { getAccessToken } = useAuth()
  const [data, setData] = useState<OverviewData | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [range, setRange] = useState<RangeValue | null>(null)

  const load = useCallback(async () => {
    if (!range) return
    try {
      const qs = new URLSearchParams({ from: range.fromISO, to: range.toISO })
      const d = await apiFetch<OverviewData>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/overview?${qs.toString()}`)
      setData(d); setErr(null)
    } catch (e) { setErr((e as Error).message) }
  }, [range, getAccessToken])
  useEffect(() => { void load() }, [load])

  if (err) return <div className="p-8"><div className="card text-red-700">Error: {err}</div></div>

  const tiles = data ? [
    { label: 'Pending payment', value: data.counts.pendingPayment, hint: 'Invoice + take payment', to: '/admin/orders?status=pending_payment' },
    { label: 'Awaiting artwork', value: data.counts.artworkAwaiting, hint: 'Approve / request revision', to: '/admin/artwork-approval' },
    { label: 'In production',   value: data.counts.inProduction, hint: 'On the press', to: '/admin/orders?status=in_production' },
    { label: 'Shipped',         value: data.counts.shipped, hint: 'Out for delivery', to: '/admin/orders?status=shipped' },
  ] : []

  const activeRangeLabel = range?.label ?? ''

  return (
    <div className="p-8">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="eyebrow">Production</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">Overview</h1>
        </div>
        {data && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.16em] text-ink-3">Revenue in range</div>
            <div className="font-heading font-bold text-2xl text-ink">${(data.revenue.cents / 100).toFixed(2)}</div>
            <div className="text-xs text-ink-3">{data.revenue.orders} order{data.revenue.orders === 1 ? '' : 's'}</div>
          </div>
        )}
      </div>

      <DateRangeFilter defaultKey="30d" onChange={setRange} />

      {!data ? <div className="text-ink-3 text-sm">Loading…</div> : (
      <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {tiles.map((t) => (
            <Link key={t.label} to={t.to} className="card hover:border-gold transition-colors block">
              <div className="text-[10px] uppercase tracking-[0.16em] text-ink-3">{t.label}</div>
              <div className="font-heading font-bold text-3xl text-ink mt-1">{t.value}</div>
              <div className="text-[11px] text-ink-3 mt-1">{t.hint}</div>
            </Link>
          ))}
        </div>

        {/* Top products + Top states */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="card">
            <div className="flex items-baseline justify-between mb-3">
              <div className="eyebrow">Top products sold</div>
              <span className="text-[11px] text-ink-3">{activeRangeLabel}</span>
            </div>
            {data.topProducts.length === 0 ? (
              <p className="text-sm text-ink-3">No line items in this range.</p>
            ) : (
              <ul className="divide-y divide-border text-sm">
                {data.topProducts.map((p, i) => (
                  <li key={p.productId} className="flex items-center gap-3 py-2">
                    <span className="font-mono text-[11px] text-ink-3 w-5">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-ink truncate">{p.name}</div>
                      <div className="text-[11px] text-ink-3 uppercase tracking-wider">{p.kind} · {p.slug}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-ink font-semibold">{p.units} unit{p.units === 1 ? '' : 's'}</div>
                      <div className="text-[11px] text-ink-3">${(p.revenueCents / 100).toFixed(2)} · {p.lineItems} line{p.lineItems === 1 ? '' : 's'}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="card">
            <div className="flex items-baseline justify-between mb-3">
              <div className="eyebrow">Top states</div>
              <span className="text-[11px] text-ink-3">By ship-to</span>
            </div>
            {data.topStates.length === 0 ? (
              <p className="text-sm text-ink-3">No orders with a shipping region in this range.</p>
            ) : (
              <ul className="divide-y divide-border text-sm">
                {data.topStates.map((s, i) => {
                  const top = data.topStates[0]!.count || 1
                  const pct = Math.min(100, Math.round((s.count / top) * 100))
                  return (
                    <li key={s.state} className="py-2">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-[11px] text-ink-3 w-5">{i + 1}</span>
                        <div className="min-w-0 flex-1 text-ink font-semibold">{s.state}</div>
                        <div className="text-right shrink-0">
                          <div className="text-ink font-semibold">{s.count} order{s.count === 1 ? '' : 's'}</div>
                          <div className="text-[11px] text-ink-3">${(s.revenueCents / 100).toFixed(2)}</div>
                        </div>
                      </div>
                      <div className="mt-1.5 h-1.5 bg-surface border border-border rounded overflow-hidden ml-8">
                        <div className="h-full bg-gold" style={{ width: pct + '%' }} />
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card">
            <div className="flex items-baseline justify-between mb-3">
              <div className="eyebrow">Recent orders</div>
              <Link to="/admin/orders" className="text-xs text-gold-dark hover:underline">View all →</Link>
            </div>
            {data.recentOrders.length === 0 ? (
              <p className="text-sm text-ink-3">No orders in this range.</p>
            ) : (
              <ul className="divide-y divide-border text-sm">
                {data.recentOrders.map((o) => (
                  <li key={o.id}>
                    <Link to={`/admin/orders/${o.id}`} className="flex items-center justify-between py-2 hover:bg-surface -mx-2 px-2 rounded transition-colors">
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-xs text-ink-3 truncate">#{o.number}</div>
                        <div className="text-ink truncate">{o.customerName ?? o.customerEmail ?? '—'}</div>
                      </div>
                      <div className="text-right ml-3">
                        <div className="font-semibold text-ink">${(o.totalCents / 100).toFixed(2)}</div>
                        <StatusBadge status={o.status} />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="card">
            <div className="eyebrow">Recent activity</div>
            {data.activity.length === 0 ? (
              <p className="text-sm text-ink-3">No production activity in this range.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.activity.map((e) => (
                  <li key={e.id} className="flex items-start gap-3 border-b border-border pb-2 last:border-0">
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-gold/15 text-gold-dark shrink-0">{e.kind.replace(/_/g, ' ')}</span>
                    <div className="min-w-0 flex-1">
                      <Link to={`/admin/orders/${e.orderId}`} className="font-mono text-xs text-ink-3 hover:text-ink">#{e.orderNumber}</Link>
                      {e.note && <div className="text-ink-2 truncate">{e.note}</div>}
                      <div className="text-[11px] text-ink-3">{new Date(e.createdAt).toLocaleString()} · {e.byEmail ?? 'system'}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </>
      )}
    </div>
  )
}
